/**
 * Utilitaires pour le formatage de valeurs monétaires
 * Supporte le formatage avec symboles, séparateurs, valeurs négatives, etc.
 */

export interface CurrencyFormatOptions {
  currencySymbol?: string;
  symbolPosition?: "left" | "right";
  decimalSeparator?: "." | ",";
  thousandSeparator?: " " | ",";
  allowNegative?: boolean;
  showNegativeInParentheses?: boolean;
  decimalDigits?: number;
  /** Si true, supprime les zéros de fin de la partie décimale (ex. 5,000 → 5 avec 3 décimales max). */
  trimTrailingZeros?: boolean;
}

/**
 * Extrait la valeur numérique brute d'une chaîne formatée
 * @param value - La valeur formatée
 * @param options - Options de formatage
 * @returns La valeur numérique brute (chaîne avec chiffres, point décimal et signe négatif)
 */
export function unmaskCurrency(value: string, options: CurrencyFormatOptions = {}): string {
  if (!value) return "";

  // Détecter si la valeur est négative (entre parenthèses ou préfixée de "-")
  const trimmedValue = value.trim();
  const isNegative = trimmedValue.startsWith("(") || trimmedValue.startsWith("-");
  
  // Retirer les parenthèses si présentes
  const valueWithoutParentheses = value.replace(/[()]/g, "");
  
  // Retirer le symbole monétaire et autres caractères non numériques (sauf séparateurs)
  const currencySymbol = options.currencySymbol || "$";
  let cleaned = valueWithoutParentheses.replace(new RegExp(`[${currencySymbol.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}]`, 'g'), "");
  cleaned = cleaned.replace(/[^\d.,\s-]/g, ""); // Garder chiffres, points, virgules, espaces et tirets
  
  // Remplacer le séparateur de milliers par rien
  const thousandSep = options.thousandSeparator || " ";
  const decimalSep = options.decimalSeparator || ".";
  
  if (thousandSep === " ") {
    // Retirer tous les espaces
    cleaned = cleaned.replace(/\s/g, "");
  }
  
  // Détecter quel séparateur est utilisé dans la saisie
  const hasPoint = cleaned.includes(".");
  const hasComma = cleaned.includes(",");
  
  // Si les deux sont présents, déterminer lequel est le séparateur décimal
  if (hasPoint && hasComma) {
    const lastPointIndex = cleaned.lastIndexOf(".");
    const lastCommaIndex = cleaned.lastIndexOf(",");
    
    // Le dernier séparateur est probablement le séparateur décimal
    if (lastPointIndex > lastCommaIndex) {
      // Le point est le séparateur décimal, retirer les virgules (séparateurs de milliers)
      cleaned = cleaned.replace(/,/g, "");
    } else {
      // La virgule est le séparateur décimal, retirer les points (séparateurs de milliers)
      cleaned = cleaned.replace(/\./g, "");
      cleaned = cleaned.replace(",", "."); // Convertir la virgule en point
    }
  } else if (hasComma && !hasPoint) {
    // Seulement des virgules
    if (decimalSep === ",") {
      // La virgule est le séparateur décimal
      // Si thousandSep est aussi ",", on assume que la dernière virgule est la décimale
      if (thousandSep === ",") {
        const parts = cleaned.split(",");
        if (parts.length > 1) {
          cleaned = parts.slice(0, -1).join("") + "." + parts[parts.length - 1];
        }
      } else {
        // La virgule est le séparateur décimal, convertir en point
        cleaned = cleaned.replace(",", ".");
      }
    } else if (/^-?,\d/.test(cleaned.trim())) {
      // Alias clavier FR : ,5 → 0.5 quand le séparateur configuré est le point
      cleaned = cleaned.trim().replace(/^(-?),/, "$10.");
    } else {
      // La virgule est le séparateur de milliers, la retirer
      cleaned = cleaned.replace(/,/g, "");
    }
  } else if (hasPoint && !hasComma) {
    // Seulement des points
    if (decimalSep === ".") {
      // Le point est le séparateur décimal
      const parts = cleaned.split(".");
      if (parts.length > 1) {
        // Si plusieurs points, garder seulement le dernier comme séparateur décimal
        if (parts.length > 2) {
          cleaned = parts.slice(0, -1).join("") + "." + parts[parts.length - 1];
        } else {
          // Un seul point, garder tel quel
          cleaned = parts.join(".");
        }
      }
    } else {
      // Le point est le séparateur de milliers, le retirer
      cleaned = cleaned.replace(/\./g, "");
    }
  }
  
  // Normaliser : s'assurer qu'il n'y a qu'un seul point décimal (seulement si nécessaire)
  // Cette normalisation est déjà faite ci-dessus pour le cas hasPoint && !hasComma
  if (decimalSep === "." && !hasComma) {
    const parts = cleaned.split(".");
    if (parts.length > 2) {
      // Plusieurs points, garder seulement le dernier comme séparateur décimal
      cleaned = parts.slice(0, -1).join("") + "." + parts[parts.length - 1];
    }
  }
  
  // Ajouter le signe négatif si nécessaire
  if (isNegative && !cleaned.startsWith("-")) {
    cleaned = "-" + cleaned.replace(/-/g, "");
  }

  // Décimale sans partie entière : .5 → 0.5, -.25 → -0.25
  if (/^-?\.\d/.test(cleaned)) {
    cleaned = cleaned.replace(/^(-?)\./, "$10.");
  }

  return cleaned;
}

/**
 * Indique si la saisie numérique est partielle (en cours de frappe), y compris `.` ou `.5` sans zéau initial.
 */
export function isPartialDecimalInput(cleanedInput: string): boolean {
  return /^-?(\d+[.,]?\d*|[.,]\d*)$/.test(cleanedInput);
}

/**
 * Formate une valeur numérique en chaîne monétaire formatée
 * @param value - La valeur à formater (nombre ou chaîne)
 * @param options - Options de formatage
 * @returns La valeur formatée
 */
export function formatCurrency(value: number | string | null | undefined, options: CurrencyFormatOptions = {}): string {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  const currencySymbol = options.currencySymbol !== undefined ? options.currencySymbol : "$";
  const symbolPosition = options.symbolPosition !== undefined ? options.symbolPosition : "left";
  const decimalSeparator = options.decimalSeparator || ".";
  const thousandSeparator = options.thousandSeparator || " ";
  const showNegativeInParentheses = options.showNegativeInParentheses || false;
  const decimalDigits = options.decimalDigits !== undefined ? options.decimalDigits : 2;

  // Convertir en nombre
  let numValue: number;
  if (typeof value === "string") {
    const cleaned = unmaskCurrency(value, options);
    numValue = parseFloat(cleaned) || 0;
  } else {
    numValue = value;
  }

  // Vérifier si négatif
  const isNegative = numValue < 0;
  const absValue = Math.abs(numValue);

  // Formater la partie entière
  const integerPart = Math.floor(absValue).toString();

  // Formater la partie décimale
  const decimalPart = absValue % 1;
  let decimalStr = decimalPart.toFixed(decimalDigits).substring(2); // Enlever "0."
  if (options.trimTrailingZeros && decimalDigits > 0) {
    decimalStr = decimalStr.replace(/0+$/, "");
  }

  // Si decimalDigits est 0 ou plus aucun chiffre décimal après trim, ne pas afficher de décimales
  const fullDecimalStr = decimalDigits > 0 && decimalStr.length > 0 ? decimalSeparator + decimalStr : "";

  // Ajouter les séparateurs de milliers
  let formattedInteger = integerPart;
  if (thousandSeparator === " ") {
    formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  } else if (thousandSeparator === ",") {
    formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  }

  // Construire la valeur formatée
  let formattedValue = formattedInteger + fullDecimalStr;

  // Gérer le signe négatif
  if (isNegative) {
    if (showNegativeInParentheses) {
      formattedValue = `(${formattedValue})`;
    } else {
      formattedValue = `-${formattedValue}`;
    }
  }

  // Ajouter le symbole
  if (symbolPosition === "left") {
    formattedValue = currencySymbol + formattedValue;
  } else {
    formattedValue = formattedValue + " " + currencySymbol;
  }

  return formattedValue;
}

/**
 * Parse la saisie utilisateur en nombre
 * @param value - La valeur saisie
 * @param options - Options de formatage
 * @returns Le nombre parsé ou null si invalide
 */
export function parseCurrencyInput(value: string, options: CurrencyFormatOptions = {}): number | null {
  if (!value || value.trim() === "") {
    return null;
  }

  const cleaned = unmaskCurrency(value, options);
  const numValue = parseFloat(cleaned);

  if (isNaN(numValue)) {
    return null;
  }

  // Valider allowNegative
  if (!options.allowNegative && numValue < 0) {
    return null;
  }

  // Arrondir selon decimalDigits si spécifié
  if (options.decimalDigits !== undefined) {
    const multiplier = Math.pow(10, options.decimalDigits);
    return Math.round(numValue * multiplier) / multiplier;
  }

  return numValue;
}

/**
 * Valide que la valeur est dans la plage autorisée [min, max]
 * @param value - La valeur à valider
 * @param min - Valeur minimale (optionnelle)
 * @param max - Valeur maximale (optionnelle)
 * @returns Objet avec isValid et message d'erreur optionnel
 */
export function validateCurrencyRange(
  value: number,
  min?: number,
  max?: number,
  options?: CurrencyFormatOptions
): { isValid: boolean; error?: string } {
  if (min !== undefined && value < min) {
    return {
      isValid: false,
      error: `La valeur doit être supérieure ou égale à ${min}`,
    };
  }

  if (max !== undefined && value > max) {
    return {
      isValid: false,
      error: `Vous dépassez le montant maximum de ${formatCurrency(max, options || {})}`,
    };
  }

  return { isValid: true };
}

/**
 * Calcule la nouvelle position du curseur après formatage
 * @param oldValue - L'ancienne valeur formatée
 * @param newValue - La nouvelle valeur formatée
 * @param oldCursorPosition - L'ancienne position du curseur
 * @param options - Options de formatage
 * @returns La nouvelle position du curseur
 */
export function getNewCursorPosition(
  oldValue: string,
  newValue: string,
  oldCursorPosition: number,
  options: CurrencyFormatOptions = {}
): number {
  // Obtenir les valeurs non formatées
  const unmaskedOld = unmaskCurrency(oldValue, options);
  const unmaskedNew = unmaskCurrency(newValue, options);

  // Compter combien de caractères numériques étaient avant le curseur dans l'ancienne valeur
  let unmaskedCharsBeforeCursor = 0;
  let digitCount = 0;
  
  for (let i = 0; i < oldCursorPosition && i < oldValue.length; i++) {
    const char = oldValue[i];
    if (/[0-9]/.test(char)) {
      digitCount++;
    }
    // Compter aussi le séparateur décimal si présent
    if ((char === "." || char === ",") && digitCount > 0) {
      // Le séparateur décimal compte comme un caractère non formaté
      unmaskedCharsBeforeCursor = digitCount;
    }
  }
  
  // Ajuster pour tenir compte des chiffres uniquement
  unmaskedCharsBeforeCursor = digitCount;

  // Déterminer si on a ajouté ou supprimé des caractères
  const oldDigits = unmaskedOld.replace(/[^\d]/g, "").length;
  const newDigits = unmaskedNew.replace(/[^\d]/g, "").length;
  const lengthDiff = newDigits - oldDigits;

  // Calculer la position cible
  let targetUnmaskedPosition = unmaskedCharsBeforeCursor;
  if (lengthDiff > 0) {
    // On a ajouté des caractères, avancer le curseur
    targetUnmaskedPosition = unmaskedCharsBeforeCursor + lengthDiff;
  } else if (lengthDiff < 0) {
    // On a supprimé des caractères, garder la position relative
    targetUnmaskedPosition = Math.max(0, unmaskedCharsBeforeCursor + lengthDiff);
  }

  // Trouver la position correspondante dans la nouvelle valeur formatée
  let newPosition = 0;
  let digitCountInNew = 0;

  for (let i = 0; i < newValue.length; i++) {
    const char = newValue[i];
    if (/[0-9]/.test(char)) {
      digitCountInNew++;
      // Placer le curseur après ce chiffre si on a atteint la position cible
      if (digitCountInNew <= targetUnmaskedPosition) {
        newPosition = i + 1;
      }
    } else {
      // Pour les caractères de formatage, avancer aussi si on n'a pas encore atteint la position cible
      if (digitCountInNew < targetUnmaskedPosition) {
        newPosition = i + 1;
      }
    }
  }

  // Si on est à la fin, placer le curseur après le dernier caractère
  if (digitCountInNew < targetUnmaskedPosition) {
    newPosition = newValue.length;
  }

  return newPosition;
}
